import {
  WidgetApi,
  MatrixCapabilities,
  WidgetApiToWidgetAction,
} from "matrix-widget-api";
import type { IOpenIDCredentials } from "matrix-widget-api";

/** Parse URL params that Element passes to widgets (query or fragment) */
function parseWidgetParams(): Record<string, string> {
  const params: Record<string, string> = {};
  const search = new URLSearchParams(window.location.search);
  const hash = new URLSearchParams(window.location.hash.replace(/^#/, "?"));
  for (const [k, v] of search) params[k] = v;
  for (const [k, v] of hash) params[k] = v;
  return params;
}

/**
 * Extract the origin from a URL string, or return "*" as fallback.
 * file:// and custom-scheme URLs return "null" which breaks postMessage,
 * so we fall back to "*" for those (common with Element Desktop / Electron).
 */
function extractOrigin(url: string | undefined): string {
  if (!url) return "*";
  try {
    const origin = new URL(url).origin;
    if (!origin || origin === "null") return "*";
    return origin;
  } catch {
    return "*";
  }
}

export interface WidgetContext {
  api: WidgetApi;
  widgetId: string;
  userId: string;
  roomId?: string;
  openIdToken: IOpenIDCredentials;
}

/**
 * Initialize the Matrix Widget API, request capabilities, and obtain
 * an OpenID Connect token for server-side identity verification.
 */
export function initWidget(): Promise<WidgetContext> {
  return new Promise((resolve, reject) => {
    const params = parseWidgetParams();
    const widgetId = params["widgetId"] || null;
    const userId = params["userId"] || "";
    const roomId = params["roomId"];
    const parentUrl = params["parentUrl"];

    // The client origin is required for postMessage to reach Element.
    // Without it, targetOrigin defaults to null and messages are silently dropped.
    const clientOrigin = extractOrigin(parentUrl);

    console.log("[CoHo Widget] Params:", {
      widgetId,
      userId,
      roomId,
      parentUrl,
      clientOrigin,
    });
    console.log("[CoHo Widget] All URL params:", params);

    const api = new WidgetApi(widgetId, clientOrigin);

    // Request capability to get OpenID token (used for server-side auth)
    api.requestCapability(MatrixCapabilities.RequiresClient);

    // Handle visibility updates from host
    api.on(
      `action:${WidgetApiToWidgetAction.UpdateVisibility}`,
      (ev: CustomEvent) => {
        ev.preventDefault();
        api.transport.reply(ev.detail, {});
      },
    );

    api.on("ready", async () => {
      console.log("[CoHo Widget] API ready, requesting OpenID token...");
      try {
        // Request an OpenID token — the host client will prompt the user
        const token = await api.requestOpenIDConnectToken();

        resolve({
          api,
          widgetId: widgetId || "cosmic-horizon",
          userId,
          roomId,
          openIdToken: token,
        });
      } catch (err) {
        reject(new Error(`Failed to get OpenID token: ${err}`));
      }
    });

    // Start the widget API communication channel
    api.start();

    // Timeout if Element never fires "ready"
    setTimeout(() => {
      reject(
        new Error(
          "Widget initialization timed out. " +
            `parentUrl=${parentUrl || "(none)"}, clientOrigin=${clientOrigin}, widgetId=${widgetId || "(none)"}. ` +
            "Check that the widget URL is correct in Element.",
        ),
      );
    }, 15_000);
  });
}

/**
 * Detect whether we are running inside a Matrix widget context.
 * Element passes widgetId and parentUrl as URL params.
 */
export function isWidgetContext(): boolean {
  const search = window.location.search + window.location.hash;
  return search.includes("widgetId=") || search.includes("parentUrl=");
}
